import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Quota, SupplierCompany } from '../../models/quota.interface';
import { ApplicationSubmitComponent } from '../application-submit/application-submit.component';
import { SupplierSelectDialogComponent } from '../../../../shared/components/supplier-select-dialog/supplier-select-dialog.component';
import { QuotaService } from '../../services/quota.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-quota-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatProgressSpinnerModule],
  templateUrl: './quota-card.component.html',
  styleUrls: ['./quota-card.component.css']
})
export class QuotaCardComponent {
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private quotaService = inject(QuotaService);
  private translate = inject(TranslateService);

  applyLoading = false;

  @Input() quota!: Quota;
  @Input() isAuthenticated = false;
  @Input() isSupplier = false;
  @Output() refreshList = new EventEmitter<void>();

  getUsagePercentage(): number {
    if (this.quota.totalLimitVolume === 0) return 0;
    return (this.quota.allocatedVolume / this.quota.totalLimitVolume) * 100;
  }

  getStatusLabel(status: string): string {
    const statusMap: Record<string, string> = {
      'ACTIVE': 'Активный',
      'INACTIVE': 'Неактивный',
      'PENDING': 'В ожидании'
    };
    return statusMap[status] || status;
  }

  getMeatTypeIcon(alias: string): string {
    const iconMap: Record<string, string> = {
      'BEEF': '🐄',
      'PORK': '🐷',
      'CHICKEN': '🐔'
    };
    return iconMap[alias] || '🍖';
  }

  getTons(kg: number): string {
    const tons = kg / 1000;
    // Если число целое, возвращаем без десятичных знаков
    if (tons % 1 === 0) {
      return tons.toString();
    }
    // Иначе форматируем с одной цифрой после запятой
    return tons.toFixed(1).replace('.', ',');
  }

  getMeatTypeName(meatType: Quota['meatType']): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || this.translate.getDefaultLang() || 'ru';
    const isKy = lang.startsWith('ky');
    return isKy ? (meatType.nameKy || meatType.nameRu) : (meatType.nameRu || meatType.nameKy);
  }

  applyForQuota(): void {
    this.applyLoading = true;

    this.quotaService.getSuppliersAll().subscribe({
      next: (response) => {
        this.applyLoading = false;
        const companies = response?.res?.content ?? [];

        if (companies.length === 0) {
          this.router.navigate(['/supplier-registration']);
          return;
        }

        const isMobile = window.innerWidth <= 480;
        const selectRef = this.dialog.open(SupplierSelectDialogComponent, {
          width: isMobile ? '100vw' : '500px',
          maxWidth: isMobile ? '100vw' : '90vw',
          data: { companies },
          disableClose: false
        });

        selectRef.afterClosed().subscribe((selected: SupplierCompany | null) => {
          if (selected) {
            this.openApplicationDialog(selected);
          }
        });
      },
      error: () => {
        this.applyLoading = false;
        this.router.navigate(['/supplier-registration']);
      }
    });
  }

  private openApplicationDialog(supplier: SupplierCompany): void {
    const dialogRef = this.dialog.open(ApplicationSubmitComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { quotaId: this.quota.id, selectedSupplier: supplier },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success && result?.refresh) {
        this.refreshList.emit();
      }
    });
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  // Показывать кнопку только для supplier или неавторизованных
  showButton(): boolean {
    return !this.isAuthenticated || this.isSupplier;
  }
}
